import { useCallback } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import axios from "axios"

/**
 * Writes logs to the backend.
 *
 * Requires the user to be authenticated.
 *
 * Do not attempt to log authentication errors.
 *
 * Logging errors will trigger the alert system.
 * Use these only in extreme cases
 *
 * @returns {log, info, error, warn, debug}
 */

const useLogging = () => {
	const { getAccessTokenSilently, user } = useAuth0()


	const log = useCallback(
		async (level, message, payload = {}) => {
			// If user is not authenticated yet, just log to console and return
			if (!user) {
				console.log(`[${level}] ${message}`, payload);
				return;
			}
			
			try {
				const token = await getAccessTokenSilently({
					audience: process.env.REACT_APP_API_IDENTIFIER
				})

				// Ensure logs match the backend format
				const logEntry = {
					message,
					level,
					// Labels as a separate field, consistent with backend logs
					labels: {
						userId: user?.sub || "unknown",
						sender: "frontEndLog"
					},
					// Spread additional fields directly into the log entry (not nested in payload)
					...payload,
					timestamp: new Date().toISOString()
				}

				// Fire and forget - don't await
				axios.post(
					`${process.env.REACT_APP_BACKEND_URL}/api/logging/logs`,
					{
						logs: [logEntry]
					},
					{
						headers: {
							Authorization: `Bearer ${token}`
						}
					}
				).catch(() => {
					// Fail silently
				})
			} catch (error) {
				// Fail silently for any errors (auth, network, etc.)
			}
		},
		[getAccessTokenSilently, user]
	)

	// Convenience methods for different log levels
	const info = useCallback((message, payload) => log("info", message, payload), [log])
	const error = useCallback(
		(message, payload) => {
			console.error(message, payload)
			log("error", message, payload)
		},
		[log]
	)
	const warn = useCallback(
		(message, payload) => {
			console.warn(message, payload)

			log("warn", message, payload)
		},
		[log]
	)
	const debug = useCallback((message, payload) => log("debug", message, payload), [log])

	return {
		log,
		info,
		error,
		warn,
		debug
	}
}

export default useLogging
